import { AnimatePresence, motion } from 'framer-motion';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from 'react';

type HeaderMenuContextValue = {
  registerOpen: () => void;
  registerClose: () => void;
};

const HeaderMenuContext = createContext<HeaderMenuContextValue | null>(null);

export function useHeaderMenuBackdrop(isOpen: boolean): void {
  const ctx = useContext(HeaderMenuContext);

  useEffect(() => {
    if (!ctx || !isOpen) {
      return undefined;
    }

    ctx.registerOpen();
    return () => {
      ctx.registerClose();
    };
  }, [isOpen, ctx]);
}

const HeaderMenuBackdrop: FC<{ visible: boolean }> = ({ visible }) => (
  <AnimatePresence>
    {visible ? (
      <motion.div
        animate={{ opacity: 1 }}
        aria-hidden
        className="site-header__backdrop"
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      />
    ) : null}
  </AnimatePresence>
);

export const HeaderMenuProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [openCount, setOpenCount] = useState(0);

  const registerOpen = useCallback(() => {
    setOpenCount((count) => count + 1);
  }, []);

  const registerClose = useCallback(() => {
    setOpenCount((count) => Math.max(0, count - 1));
  }, []);

  const value = useMemo(
    () => ({ registerOpen, registerClose }),
    [registerOpen, registerClose],
  );

  return (
    <HeaderMenuContext.Provider value={value}>
      {children}
      <HeaderMenuBackdrop visible={openCount > 0} />
    </HeaderMenuContext.Provider>
  );
};
